/**
 * Copyright (c) 2026 Accunai India Services Pvt. Ltd. All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This file is part of proprietary software owned by Accunai India Services Pvt. Ltd.
 * Unauthorized copying, distribution, modification, or use of this file,
 * in whole or in part, via any medium, is strictly prohibited without
 * prior written permission from Accunai India Services Pvt. Ltd.
 *
 * Generated with GitHub Copilot Business/Enterprise.
 * Reviewed and approved by: AI-assisted / 2026-08-12
 */

import React, { Suspense, lazy, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress } from "@mui/material";
import type { RootState } from "state/store/appStore";
import type { AppDispatch } from "state/store/appStore";
import { getUserAccess } from "state/slices/company";
import { getAuthTokenCookie } from "../shared/auth/cookieStore";
import SidebarLayout from "../layout/SidebarLayout";

const LoginPage = lazy(() => import("../pages/login/LoginPage"));
const DashboardPage = lazy(() => import("../pages/dashboard/DashboardPage"));
const ProjectsDashboardPage = lazy(() => import("../pages/projects/ProjectsDashboardPage"));
const ProjectWorkspacePage = lazy(() => import("../pages/projects/ProjectWorkspacePage"));
const AdminsPage = lazy(() => import("../pages/management/AdminsPage"));
const ExpertsPage = lazy(() => import("../pages/management/ExpertsPage"));
const VendorsPage = lazy(() => import("../pages/management/VendorsPage"));
const ProjectEditorPage = lazy(() => import("../pages/editors/ProjectEditorPage"));
const UserEditorPage = lazy(() => import("../pages/editors/UserEditorPage"));
const ExpertEditorPage = lazy(() => import("../pages/editors/ExpertEditorPage"));
const VendorEditorPage = lazy(() => import("../pages/editors/VendorEditorPage"));
const UploadTaskPage = lazy(() => import("../features/upload/pages/UploadTaskPage"));
const BulkTaskDeletePage = lazy(() => import("../features/upload/pages/BulkTaskDeletePage"));
const DeliveredTaskUploadPage = lazy(() => import("../features/upload/pages/DeliveredTaskUploadPage"));
const UploadQuestionsPage = lazy(() => import("../features/upload/pages/UploadQuestionsPage"));
const ExportJsonPage = lazy(() => import("../features/upload/pages/ExportJsonPage"));
const WorkflowPage = lazy(() => import("../features/workflow/pages/WorkflowPage"));
const VendorBulkAllocationPage = lazy(() => import("../features/upload/pages/VendorBulkAllocationPage"));
const VendorBulkUnallocationPage = lazy(() => import("../features/upload/pages/VendorBulkUnallocationPage"));
const TaskAllocationPage = lazy(() => import("../features/upload/pages/TaskAllocationPage"));
const TaskCompletedMain = lazy(() => import("../features/tasks/components/task-completed/taskCompletedMain"));
const TestQuestionsMain = lazy(() => import("../features/tasks/components/project-test/testQuestions/testQuestionsMain"));
const ReviewEditorPage = lazy(() => import("../features/review/pages/ReviewEditorPage"));
const ConfigMain = lazy(() => import("../routes/addConfig/configMain"));
const CreateFeedbackPage = lazy(() => import("../features/feedback/pages/CreateFeedbackPage"));
const AiValidationHistoryPage = lazy(() => import("../features/history/pages/AiValidationHistoryPage"));
const ScriptExecutionPage = lazy(() => import("../features/script-execution/pages/ScriptExecutionPage"));
const NotFoundPage = lazy(() => import("../pages/not-found/NotFoundPage"));

const RouteLoadingFallback: React.FC = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
    <CircularProgress />
  </Box>
);

const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const dashboardAccess = useSelector(
    (state: RootState) => state.company.company.data.modules.dashboard
  );

  // Tracks the token we last fetched access for, so re-navigating after a client-side
  // login (no page reload) still refreshes company/module access instead of staying stale.
  const fetchedForTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const authToken = getAuthTokenCookie();

    if (!authToken) {
      fetchedForTokenRef.current = null;
      navigate("/new/login", { replace: true });
      return;
    }

    if (authToken !== fetchedForTokenRef.current) {
      fetchedForTokenRef.current = authToken;
      dispatch(getUserAccess({}));
    }
  }, [location.pathname, navigate, dispatch]);

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path="/new/login" element={<LoginPage />} />
        <Route path="/login" element={<Navigate to="/new/login" replace />} />

        <Route path="/" element={<SidebarLayout />}>
          {dashboardAccess != null && dashboardAccess.access === true && (
            <Route path="/dashboard" element={<DashboardPage />} />
          )}

          <Route path="projects" element={<ProjectsDashboardPage />} />
          <Route path="project/:id" element={<ProjectWorkspacePage />} />

          <Route path="users" element={<AdminsPage />} />
          <Route path="experts" element={<ExpertsPage />} />
          <Route path="vendors" element={<VendorsPage />} />

          <Route path="addproject" element={<ProjectEditorPage />} />
          <Route path="addproject/:id" element={<ProjectEditorPage />} />
          <Route path="adduser" element={<UserEditorPage />} />
          <Route path="adduser/:id" element={<UserEditorPage />} />
          <Route path="addexpert" element={<ExpertEditorPage />} />
          <Route path="addexpert/:id" element={<ExpertEditorPage />} />
          <Route path="addvendor" element={<VendorEditorPage />} />
          <Route path="addvendor/:id" element={<VendorEditorPage />} />

          <Route path="addconfig/:id" element={<ConfigMain />} />
          <Route path="editconfig/:id/:configId/:configRowId/" element={<ConfigMain />} />

          <Route path="uploadtask/:id" element={<UploadTaskPage />} />
          <Route path="bulktaskdelete/:id" element={<BulkTaskDeletePage />} />
          <Route path="deliveredTask/:id" element={<DeliveredTaskUploadPage />} />
          <Route path="uploadQuestions/:id" element={<UploadQuestionsPage />} />

          <Route path="allocation/:id/:option" element={<TaskAllocationPage />} />
          <Route path="vendorbulkallocation/:id" element={<VendorBulkAllocationPage />} />
          <Route path="vendorbulkunallocation/:id" element={<VendorBulkUnallocationPage />} />

          <Route path="task/:id/:taskId" element={<TaskCompletedMain />} />
          <Route path="addtest/:id" element={<TestQuestionsMain />} />
          <Route path="edittest/:id/:configId/:configRowId/" element={<TestQuestionsMain />} />

          <Route path="addReviewer/:id" element={<ReviewEditorPage />} />
          <Route path="editReviewer/:id/:reviewerId" element={<ReviewEditorPage />} />

          <Route path="exportjson/:id" element={<ExportJsonPage />} />
          <Route path="createWorkflow/:id" element={<WorkflowPage />} />
          <Route path="createWorkflow/:id/:workflowId" element={<WorkflowPage />} />

          <Route path="project/:id/ai-validation-history" element={<AiValidationHistoryPage />} />
          <Route path="addfeedback/:id" element={<CreateFeedbackPage />} />
          <Route path="feedback/edit/:id/:feedbackId" element={<CreateFeedbackPage />} />

          <Route path="script-execution" element={<ScriptExecutionPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
